import { useDispatch, useSelector } from 'react-redux';
import { ComponentProps, FormEventHandler, MouseEvent, PropsWithChildren, useEffect } from 'react';
import { Col, Container, Form, Row, Surface, useBreakpoints } from '@vst/beam';

import type { FormValue } from '@vst/shared/lib/form';
import { appActions, selectIsCartVisible } from '@buy-viasat/redux/src/app';
import { Routes, selectCurrentAppRoute, selectStepNumber, selectTotalSteps } from '@buy-viasat/redux/src/navigator';

import styles from './checkoutLayout.module.scss';
import { CheckoutCartHeader } from '../CheckoutCart/CheckoutCartHeader';
import { CheckoutCart, Divider, Header, LoadingSpinner, Navigator, ProgressBar } from '../..';
import { LoadingInstallationDates } from '../LoadingSpinner/loadingInstallationDates';
import { useTranslation } from 'react-i18next';

type CheckoutLayoutProps = {
  loading?: boolean;
  loadingText?: string;
  className?: string;
  headerProps: ComponentProps<typeof Header>;
  onConfirm: FormEventHandler<HTMLFormElement>;
  checkoutDisclosure?: React.ReactNode | null;
  progressProps: Omit<ComponentProps<typeof ProgressBar>, 'initialStep' | 'totalNumberOfSteps'>;
  cartProps: Omit<ComponentProps<typeof CheckoutCart>, 'displayMobileCart'>;
  navigatorProps: ComponentProps<typeof Navigator>;
  initialValues?: Record<string, FormValue>;
  handleSkipScheduleInstallationClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

const CheckoutActions = (props: Pick<CheckoutLayoutProps, 'checkoutDisclosure' | 'navigatorProps' | 'loading'>) => {
  const { checkoutDisclosure, navigatorProps, loading } = props;
  const { viewport } = useBreakpoints();

  return (
    <>
      {checkoutDisclosure ? (
        <Surface pt={{ xs: '24px', md: '40px' }} pb={{ md: '24px', sm: '32px' }}>
          {checkoutDisclosure}
        </Surface>
      ) : null}
      <Surface>
        {viewport.largerOrWithin.md && <Divider position="outside-2" mt="24px" />}
        <Navigator {...navigatorProps} disabled={loading} noTopPadding={true} />
      </Surface>
    </>
  );
};

export const CheckoutLayout = (props: PropsWithChildren<CheckoutLayoutProps>) => {
  const {
    loading = false,
    loadingText = '',
    className = '',
    headerProps,
    progressProps,
    navigatorProps,
    checkoutDisclosure,
    children,
    onConfirm,
    initialValues,
    cartProps,
    handleSkipScheduleInstallationClick = () => null,
  } = props;

  const dispatch = useDispatch();
  const { viewport } = useBreakpoints();

  const displayMobileCart = useSelector(selectIsCartVisible);
  const stepNumber = useSelector(selectStepNumber);
  const totalNumberOfSteps = useSelector(selectTotalSteps);

  const onCheckoutCartHeaderClick = () => dispatch(appActions.setIsCartVisible(!displayMobileCart));
  const { t } = useTranslation(['scheduleInstallation']);
  const currentRoute = useSelector(selectCurrentAppRoute);
  const isLookingForInstallationDates = loading && currentRoute === Routes.SCHEDULE_INSTALLATION;
  const isPaymentsPage = currentRoute === Routes.PAYMENT_INFORMATION;
  const checkoutActionsContainerStyle = loading
    ? styles['loading']
    : isPaymentsPage
      ? styles['payments-page-loaded']
      : styles['loaded'];

  useEffect(() => {
    dispatch(appActions.setIsCartVisible(false));
  }, [dispatch]);

  return (
    <Surface variant="secondary" className={styles['root']}>
      <Surface className={styles['header']} data-testid="header">
        <Container fluidUntil={'xl'} className={styles['headerContainer']}>
          <Row edge2edge>
            <Col>
              <Header {...headerProps} />
            </Col>
          </Row>
        </Container>
      </Surface>

      <Form onSubmit={onConfirm} id="checkoutForm" initialValues={initialValues} autoComplete={'off'}>
        <Surface className={styles['main']} variant="secondary" mx={{ lg: '48px' }}>
          {viewport.smallerOrWithin.sm && (
            <>
              <CheckoutCartHeader
                onClick={onCheckoutCartHeaderClick}
                displayMobileCart={displayMobileCart}
                dueMonthly={cartProps.translationKeys.mobile.dueMonthlyTitle}
                dueToday={cartProps.translationKeys.mobile.dueTodayTitle}
              />
              <CheckoutCart displayMobileCart={displayMobileCart} {...cartProps} />
            </>
          )}
          <ProgressBar initialStep={stepNumber} totalNumberOfSteps={totalNumberOfSteps} {...progressProps} />
          <Container fluidUntil="md" pb={{ md: '72px' }} pt={{ md: '8px' }}>
            <Row edge2edge>
              <Col lg={8}>
                <Surface
                  responsive
                  className={`${styles['content']} ${className} ${loading ? styles['loadingContent'] : ''}`}
                >
                  {isLookingForInstallationDates ? (
                    <LoadingInstallationDates
                      skipButtonText={t('scheduleInstallation.link.skipAndContinue')}
                      headingTitle={t('scheduleInstallation.subheading.title.selectDate')}
                      handleSkipLinkClick={handleSkipScheduleInstallationClick}
                    >
                      <LoadingSpinner size="large" text={loadingText} />
                    </LoadingInstallationDates>
                  ) : loading ? (
                    <LoadingSpinner size="large" text={loadingText} />
                  ) : null}
                  <Surface responsive className={checkoutActionsContainerStyle}>
                    <div>{children}</div>
                    {viewport.largerThan.sm ? (
                      <CheckoutActions
                        navigatorProps={navigatorProps}
                        checkoutDisclosure={checkoutDisclosure}
                        loading={loading}
                      />
                    ) : null}
                  </Surface>
                </Surface>
              </Col>
              <Col lg={4} className={styles['asideContainer']}>
                <Surface responsive className={styles['aside']} data-testid="cart">
                  {viewport.smallerOrWithin.sm ? (
                    <CheckoutActions
                      navigatorProps={navigatorProps}
                      checkoutDisclosure={checkoutDisclosure}
                      loading={loading}
                    />
                  ) : (
                    <CheckoutCart displayMobileCart={displayMobileCart} {...cartProps} />
                  )}
                </Surface>
              </Col>
            </Row>
          </Container>
        </Surface>
      </Form>
    </Surface>
  );
};

export default CheckoutLayout;
